import { Button, createStyles, Divider, Grid, makeStyles, TextField, Theme, Typography } from "@material-ui/core";
import { ILoginFormValues } from "../_interfaces/ILoginFormValues";
import { useContext, useState } from "react";
import { RootContext } from "../../../stores";
import { Alert } from "@material-ui/lab";
import FormLoadingSpinner from "../../../components/Organization/VolunteerOpportunities/FormLoadingSpinner";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        form: {
            position: 'relative'
        },
        forgottenPassword: {
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(2),
            textAlign: 'center'
        },
        link: {
            color: theme.palette.primary.main,
            cursor: 'pointer',
            '&:hover': {
                textDecoration: 'underline'
            }
        },
        field: {
            display: 'flex',
            flexGrow: 1,
        },
        loginButton: {
            marginTop: theme.spacing(2)
        },
        linkWrapper: {
            marginTop: theme.spacing(2),
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),
            color: theme.palette.text.secondary,
            textAlign: 'center'
        },
        signUpButton: {
            marginTop: theme.spacing(1),
            color: theme.palette.common.white,
            background: theme.palette.success.main,
            '&:hover': {
                background: theme.palette.success.main,
            }
        },
        alert: {
            marginBottom: theme.spacing(2),
            flexGrow: 1,
            textAlign: 'left'
        },
    }),
);

interface LoginFormProps {
    values: ILoginFormValues;
    onChange: (patch: Partial<ILoginFormValues>) => void;
    onSubmit: (override?: Partial<ILoginFormValues>) => void;
    isSubmitting?: boolean;
    error?: string;
    fieldErrors?: Partial<Record<keyof ILoginFormValues, string>>;
}

export const LoginForm: React.FC<LoginFormProps> = ({
    values,
    onChange,
    onSubmit,
    isSubmitting,
    error,
    fieldErrors
}) => {

    /********** State **********/

    const getFormId = () => {
        return `form-login-${Date.now()}-${Math.random()}`;
    }

    const [formId] = useState(() => getFormId());

    /********** React hooks **********/

    const classes = useStyles();
    const rootStore = useContext(RootContext);

    /********** Event handlers **********/

    const onForgotPassword = () => {
        rootStore.navigationStore.appwideDialogStates.forgotPasswordDialog.setOpen(true);
    }

    const onLoginClicked = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault(); // Prevent page reload
        event.stopPropagation();
        onSubmit();
    }

    const onSignUpClicked = (event: React.FormEvent<HTMLButtonElement>) => {
        event.preventDefault(); // Prevent page reload
        event.stopPropagation();
        onChange({ hasAccount: false })
        onSubmit({ hasAccount: false });
    }

    const passwordFieldError = fieldErrors?.password || error;

    /********** Render **********/

    return (
        <form onSubmit={onLoginClicked} noValidate autoComplete="off" id={formId} className={classes.form}>
            {values.loading ? <FormLoadingSpinner /> : undefined}
            {error && <Alert severity="error" className={classes.alert}>{error}</Alert>}
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        id="standard-username-input"
                        label="Email"
                        autoComplete="current-username"
                        variant="outlined"
                        required
                        value={values.username}
                        error={error !== undefined || (fieldErrors?.username !== undefined && fieldErrors?.username.length > 0)}
                        helperText={fieldErrors?.username}
                        className={classes.field}
                        onChange={(e) => onChange({ username: e.target.value })}
                        autoFocus
                        {...values.fieldProperties?.username}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id="standard-password-input"
                        label="Password"
                        type="password"
                        autoComplete="current-password"
                        variant="outlined"
                        className={classes.field}
                        required
                        value={values.password}
                        error={passwordFieldError !== undefined && passwordFieldError.length > 0}
                        helperText={fieldErrors?.password}
                        onChange={(e) => onChange({ password: e.target.value })}
                        {...values.fieldProperties?.password}
                    />
                </Grid>
            </Grid>
            <Button color="primary" variant="contained" type="submit" form={formId} fullWidth className={classes.loginButton} size="large" disabled={isSubmitting}>
                Login
            </Button>
            <div className={classes.forgottenPassword}>
                <Typography variant="body2" className={classes.link} onClick={onForgotPassword}>
                    Forgot your password?
                </Typography>
            </div>
            <Divider />
            <div className={classes.linkWrapper}>
                <Typography variant="body2">
                    {`Don't have an account yet? `}
                </Typography>
                <Button
                    color="inherit"
                    variant="contained"
                    fullWidth
                    className={classes.signUpButton}
                    size="large"
                    onClick={onSignUpClicked}
                    disabled={isSubmitting}
                >
                    Sign up
                </Button>
            </div>
        </form>
    );
};
